import React, { useEffect } from 'react';
import { useTheme } from 'next-themes';
import { Toaster as Sonner, useSonner } from 'sonner';
import { CheckIcon, CircleAlertIcon } from 'lucide-react';

// NOTE: sonner's stylesheet is imported from the app's Tailwind entry
// (packages/web/app/tailwind.css), ahead of the Tailwind utilities, so the
// classNames below win the equal-specificity cascade against sonner's base
// styles (BB-3513). It is deliberately NOT imported here: a component-level
// import lands in a separate chunk whose order vs the utilities is not
// guaranteed, which would let sonner's base button/toast styles override these.
// The island-style enter/exit motion also lives in that entry, as scoped
// attribute selectors overriding sonner's zero-specificity `:where()` rules.

type ToasterProps = React.ComponentProps<typeof Sonner>;

// The Magic Island's berth: 2rem above the bottom edge, clear of any notch
// (see packages/web/app/ui-rocco/pages/detail-shared/magic-island).
const TOAST_BOTTOM_OFFSET = 'calc(2rem + env(safe-area-inset-bottom))';

// The island pill's grow motion, reused when a mounted toast changes size —
// the same overshoot that brings the toast in.
const GROW_DURATION_MS = 450;
const GROW_EASING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/**
 * Animates a mounted toast's box when its content changes size — a
 * `toast.loading` upsert resolving to a longer message, or text wrapping onto
 * a second line. Sonner re-renders content instantly, so without this the
 * pill snaps; the island's equivalent (a framer `layout` morph) is what this
 * mirrors. Width and height animate together, as one grow.
 */
const useToastGrow = () => {
    const { toasts } = useSonner();
    const count = toasts.length;

    useEffect(() => {
        if (count === 0) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }
        const seen = new WeakMap<Element, { w: number; h: number }>();
        const timers = new WeakMap<Element, number>();
        const growing = new WeakMap<Element, Animation>();
        // A resize burst (window resize, font settle) fires the observer many
        // times in a row; animating each step stacks dozens of concurrent
        // animations. Instead the first change pins the from-size, a short
        // settle timer keeps deferring while changes keep coming, and one
        // animation then runs from the pinned size to wherever the box ended
        // up — cancelling any grow still in flight.
        const SETTLE_MS = 90;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const el = entry.target as HTMLElement;
                const prev = seen.get(el);
                const next = { w: el.offsetWidth, h: el.offsetHeight };
                // First observation is the baseline; the enter animation is
                // transform-only, so any later box change is a content change.
                if (!prev) {
                    seen.set(el, next);
                    continue;
                }
                if (prev.w === next.w && prev.h === next.h) continue;
                const pending = timers.get(el);
                if (pending) clearTimeout(pending);
                timers.set(
                    el,
                    window.setTimeout(() => {
                        timers.delete(el);
                        const settled = {
                            w: el.offsetWidth,
                            h: el.offsetHeight,
                        };
                        seen.set(el, settled);
                        if (settled.w === 0 || settled.h === 0) return;
                        if (prev.w === settled.w && prev.h === settled.h) {
                            return;
                        }
                        growing.get(el)?.cancel();
                        el.style.overflow = 'clip';
                        const grow = el.animate(
                            [
                                {
                                    width: `${prev.w}px`,
                                    height: `${prev.h}px`,
                                },
                                {
                                    width: `${settled.w}px`,
                                    height: `${settled.h}px`,
                                },
                            ],
                            {
                                duration: GROW_DURATION_MS,
                                easing: GROW_EASING,
                            },
                        );
                        growing.set(el, grow);
                        grow.onfinish = () => {
                            growing.delete(el);
                            el.style.removeProperty('overflow');
                        };
                    }, SETTLE_MS),
                );
            }
        });
        document
            .querySelectorAll('[data-sonner-toast]')
            .forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [count]);
};

/**
 * The site-wide toast, styled as the Magic Island's dark glass pill: a
 * content-hugging fully-rounded bar at bottom centre, white text on
 * translucent black, with white primary / ghost secondary action buttons.
 */
export const Toaster = ({ ...props }: ToasterProps) => {
    const { theme = 'system' } = useTheme();
    useToastGrow();

    return (
        <Sonner
            theme={theme as ToasterProps['theme']}
            className="toaster group"
            position="bottom-center"
            duration={5000}
            offset={{ bottom: TOAST_BOTTOM_OFFSET }}
            // left/right must be 0: sonner's mobile stylesheet gives the band
            // `width: 100%` PLUS `left: <offset>` (right is ignored once width
            // is set), so any non-zero offset shifts the whole band — and the
            // mx-auto pill inside it — off centre by that amount. The pill
            // keeps its own edge clearance via max-w below.
            mobileOffset={{ bottom: TOAST_BOTTOM_OFFSET, left: 0, right: 0 }}
            toastOptions={{
                style: {
                    // Zero out sonner's icon/button nudge margins; the pill
                    // spaces its children with a flex gap instead.
                    // @ts-expect-error `We need CSS variables here`
                    '--toast-button-margin-start': '0',
                    '--toast-icon-margin-start': '0',
                    '--toast-icon-margin-end': '0',
                    '--toast-svg-margin-start': '0',
                    '--toast-svg-margin-end': '0',
                },
                classNames: {
                    // Most props are `!`-important because sonner's base
                    // `:where([data-sonner-toast][data-styled='true'])` rule
                    // (background / border-radius / padding / border / color /
                    // width / gap / box-shadow) is UN-LAYERED, and in Tailwind
                    // v4 utilities live in `@layer utilities`. Un-layered CSS
                    // always beats any `@layer`, regardless of specificity, so
                    // plain utilities lose (BB-3732). `!important` beats
                    // un-layered normal declarations. `left/right/mx-auto`
                    // centre the content-hugging pill inside the toaster band
                    // (sonner's own mobile media query sets left/right/width).
                    toast: 'group toast group-[.toaster]:items-center group-[.toaster]:gap-4! group-[.toaster]:w-fit! group-[.toaster]:max-w-[calc(100%-2rem)] group-[.toaster]:md:max-w-full group-[.toaster]:left-0! group-[.toaster]:right-0! group-[.toaster]:mx-auto! group-[.toaster]:min-h-[4.375rem] group-[.toaster]:py-2! group-[.toaster]:pl-8! group-[.toaster]:pr-8! group-[.toaster]:has-[button]:pr-2! group-[.toaster]:border-0! group-[.toaster]:bg-black/55! group-[.toaster]:text-white! group-[.toaster]:rounded-full! group-[.toaster]:font-sans group-[.toaster]:backdrop-blur-2xl group-[.toaster]:shadow-[0_8px_30px_rgba(0,0,0,0.3)]! group-[.toaster]:ring-1 group-[.toaster]:ring-white/10',
                    title: 'group-[.toast]:text-lg group-[.toast]:font-medium',
                    description: 'group-[.toast]:text-white/60!',
                    icon: 'group-[.toast]:size-7!',
                    // Primary action: the island's white accent. Geometry AND
                    // colours are `!`-important — sonner's un-layered
                    // `[data-button]` rule sets radius, padding, height,
                    // font-size, colours and transition, so every overridden
                    // prop needs importance to apply at all. Per-toast button
                    // restyling is not supported; actions needing a bespoke
                    // look (e.g. the design-board arrow circle) pass a custom
                    // element in the `action`/`cancel` slot instead, which
                    // sonner renders verbatim without `[data-button]`.
                    actionButton:
                        'group-[.toast]:shrink-0 group-[.toast]:rounded-full! group-[.toast]:h-13.5! group-[.toast]:px-7! group-[.toast]:text-lg! group-[.toast]:font-medium group-[.toast]:tracking-normal group-[.toast]:bg-white! group-[.toast]:text-gray-900! group-[.toast]:hover:bg-gray-100! group-[.toast]:transition-colors!',
                    // Secondary action: a ghost pill on the glass.
                    cancelButton:
                        'group-[.toast]:shrink-0 group-[.toast]:rounded-full! group-[.toast]:h-13.5! group-[.toast]:px-7! group-[.toast]:text-lg! group-[.toast]:font-normal group-[.toast]:tracking-normal group-[.toast]:bg-white/15! group-[.toast]:text-white! group-[.toast]:hover:bg-white/25! group-[.toast]:transition-colors!',
                },
            }}
            icons={{
                success: <CheckIcon className="size-7" strokeWidth={2} />,
                error: (
                    <CircleAlertIcon
                        className="size-7 text-red-400"
                        strokeWidth={2}
                    />
                ),
            }}
            style={{
                // @ts-expect-error `We need a CSS variable here`
                '--width': 'min(56.125rem, 90dvw)',
                // vaul/Radix drawers and dialogs lock `body { pointer-events: none }`
                // while open. The toaster portal lives on the body, and sonner never
                // re-enables pointer events on visible toasts, so its buttons (e.g. the
                // design-board "View board" action) become unclickable while a drawer is
                // open. Re-enable them here so toasts stay interactive above any open
                // drawer/dialog (BB-3752). Hidden toasts keep `pointer-events: none`
                // from sonner's own stylesheet, so this only affects visible ones.
                pointerEvents: 'auto',
            }}
            {...props}
        />
    );
};
